import { FunctionComponent, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import {
  BatchApiStatusHandler,
  Button,
  CircleAddIcon,
  formSpecToRendererConfig,
  Renderer,
  SectionHeading,
} from '../components';
import { useBatchApi } from '../hooks';
import { Invite, User } from '../types';
import { STRIPE_BILLING_URL } from '../settings/config';
import { fetchApi } from '../utils/fetchApi';
import { ApiStatus } from '../utils/types';
import { useGlobalContext } from '../contexts/globalContext';
import { FormError } from '../components/forms/FormError';

type AccountProps = {
  users: User[];
  invites: Invite[];
};

type FormValues = {
  firstName?: string;
  lastName?: string;
};

export const Account: FunctionComponent<AccountProps> = ({
  users,
  invites,
}) => {
  const { globalState, setGlobalState } = useGlobalContext();

  const handleManageBillingClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    const { status, data, error } = await fetchApi({
      url: '/api/subscription/billing',
      method: 'GET',
    });
    if (status === ApiStatus.Success) {
      window.open(data.customer_portal_url, '_blank')?.focus();
    } else {
      // Fallback to the no-code customer portal link
      console.error(error);
      window.open(STRIPE_BILLING_URL, '_blank')?.focus();
    }
  };

  const formMethods = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {
      firstName: globalState.user.first_name || '',
      lastName: globalState.user.last_name || '',
    },
  });
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const filteredInvites = invites.filter((invite) => !invite.is_accepted);
  const mergedUsersAndInvites = [...users, ...filteredInvites];

  const formFields = [
    {
      name: 'firstName',
      label: 'First name',
      required: 'This is required',
      component: {
        type: 'text',
      },
    },
    {
      name: 'lastName',
      label: 'Last name',
      required: 'This is required',
      component: {
        type: 'text',
      },
    },
  ];

  const {
    handleSubmit,
    clearErrors,
    reset,
    formState: { errors, isDirty, isValid },
  } = formMethods;

  const loadedSpec = {
    title: 'Profile',
    sections: [{ form_fields: formFields }],
  };

  const onCancel = () => {
    reset();
  };

  const onSubmit = async (formData: FormValues) => {
    if (!isValid) return;

    setHasSubmitError(false);
    setIsLoading(true);
    clearErrors();

    const userResponse = await fetchApi({
      url: '/api/users/me',
      method: 'PUT',
      body: {
        first_name: formData.firstName,
        last_name: formData.lastName,
      },
    });

    const { status, data: userResponseData } = userResponse;

    if (status === ApiStatus.Error || status === ApiStatus.ErrorUnauthorized) {
      setHasSubmitError(true);
    } else {
      reset({
        firstName: userResponseData.first_name,
        lastName: userResponseData.last_name,
      });
      setGlobalState((oldState) => ({
        ...oldState,
        user: {
          ...oldState.user,
          first_name: userResponseData.first_name,
          last_name: userResponseData.last_name,
        },
      }));
      setSubmitSuccess(true);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (!isDirty) return;

    // Reset validation messages if needed when the user touchs form inputs
    if (hasSubmitError) {
      setHasSubmitError(false);
    } else if (submitSuccess) {
      setSubmitSuccess(false);
    }
  }, [isDirty]);

  return (
    <div className="flex flex-col">
      <div className="p-16">
        <SectionHeading className="flex-1 pb-4" text="Account" />

        <div className="flex flex-col py-9 xl:max-w-xl">
          <section>
            <FormProvider {...formMethods}>
              <form>
                <Renderer
                  config={formSpecToRendererConfig(loadedSpec)}
                  errors={errors}
                />
                {hasSubmitError && <FormError margin="mb-9" />}
                {submitSuccess && (
                  <div>
                    <div className="mb-9 w-full rounded border border-green-200 bg-green-100 px-4 py-2 text-green-700">
                      Your profile information has been updated!
                    </div>
                  </div>
                )}
              </form>
            </FormProvider>
          </section>
          <hr className="mb-6" />
          <section>
            <h3 className="text-lg font-medium">Billing</h3>

            <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="text-sm font-medium text-gray-700">
                Your Billing Plan
              </h4>
              <div className="flex space-x-2">
                <p className="mt-2 flex-1 text-sm text-gray-700">
                  Manage your subscription and billing information.
                </p>

                <div className="flex items-start">
                  <Button
                    type="button"
                    size="small"
                    onClick={handleManageBillingClick}
                  >
                    Manage Billing
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <hr className="mb-6 mt-9" />

          <section>
            <div className="my-6 flex items-center justify-between">
              <h3 className="text-lg font-medium">Team</h3>

              <Button
                type="button"
                size="small"
                variant="secondary"
                leftIcon={<CircleAddIcon className="size-4 text-sage-600" />}
                onClick={() => navigate('/settings/invite')}
              >
                Invite People
              </Button>
            </div>

            <table className="w-full border" cellPadding="0">
              <thead>
                <tr className="border-b text-sm uppercase text-gray-500">
                  <th className="px-6 py-3 text-left font-medium tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left font-medium tracking-wider">
                    Email
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y">
                {mergedUsersAndInvites.length > 0 ? (
                  mergedUsersAndInvites.map((user) => {
                    const isInvite = 'is_accepted' in user;

                    return (
                      <tr key={isInvite ? user.email : user.id}>
                        <td>
                          <div className="w-full">
                            <p
                              className={cn('p-6 font-medium', {
                                'text-gray-400': isInvite,
                                'text-gray-900': !isInvite,
                              })}
                            >
                              {isInvite
                                ? 'Invite Pending'
                                : `${user.first_name || ''} ${
                                    user.last_name || ''
                                  }`}
                            </p>
                          </div>
                        </td>
                        <td>
                          <div className="w-full">
                            <p
                              className={cn('p-6', {
                                'text-gray-400': isInvite,
                                'text-gray-900': !isInvite,
                              })}
                            >
                              {user.email || ''}
                            </p>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6}>
                      <div className="flex h-64 items-center justify-items-center">
                        <div className="m-auto">
                          <p className="italic text-gray-400">
                            No team members found.
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        </div>
      </div>
      {isDirty && isValid && (
        <div className="b-0 l-0 sticky bottom-0 flex h-[72px] items-center justify-end space-x-2 border-t border-gray-200 bg-sage-50 px-6 py-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <div className="w-28">
            <Button
              type="submit"
              onClick={handleSubmit(onSubmit)}
              isFullWidth
              isLoading={isLoading}
              disabled={isLoading}
            >
              Save Changes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const AccountView: FunctionComponent = () => {
  const batchResponse = useBatchApi([
    {
      url: '/api/users',
      method: 'GET',
    },
    {
      url: '/api/invites',
      method: 'GET',
    },
  ]);

  const componentPropsFn = ([
    usersResponse,
    inviteResponse,
  ]: any[]): AccountProps => {
    return {
      users: usersResponse,
      invites: inviteResponse,
    };
  };

  return (
    <BatchApiStatusHandler
      batchResponse={batchResponse}
      component={Account}
      componentProps={componentPropsFn}
    />
  );
};
